import React from 'react';
import RadioRhf from '../components/RadioRhf/RadioRhf';
import { RadioOptionRhf } from '../components/RadioRhf/RadioOptionRhf';
import NewPatientPhotos from '../../NewPatient/Photos/NewPatientPhotos';
import { FormQuestion } from '../../../pages/DiffRetainer/type';
import { Controller } from 'react-hook-form';
import useDiffRetainerLangContext from './hooks/useDiffRetainerLangContext';
import CustomContainer from './CustomContainer';
import { useLang } from '../../../util/lang';
import { baseImpressionOption } from '../../../pages/DiffRetainer/utils';
import { CaseImpressionFileType } from '../../../pages/DiffRetainer/modal/type';

/**
 * `FileUploadQuestion` is a component that renders a question with file upload functionality.
 *
 * @component
 * @param {Object} props - The props that define the component.
 * @param {string} props.impressionType - The type of impression.
 * @param {FormQuestion} props.question - The question object.
 * @param {Object} props.errors - The form validation errors.
 * @param {Object} props.control - The form control object.
 * @param {string} props.defaultValue - The default value of the form field.
 * @param {boolean} [props.enableUploadImpression=false] - A flag that indicates whether the impression upload functionality is enabled.
 * @param {boolean} props.threeshapeConnected - A flag that indicates whether the 3Shape system is connected.
 * @param {Function} props.setThreeshapeConnected - A function that updates the `threeshapeConnected` state.
 * @param {boolean} isNewCase - Boolean to indicate if the case is new.
 * @param {boolean} isReSubmit - Boolean to indicate if the case is resubmit (for incompleted new case).
 * @returns {ReactElement} The rendered React element.
 */

const FileUploadQuestion = ({
    impressionType,
    question,
    errors,
    control,
    defaultValue,
    enableUploadImpression = false,
    threeshapeConnected,
    setThreeshapeConnected,
    isNewCase = false,
    isReSubmit = false,
}) => {
    // if is new case, use existing Impression Tab for handling impression upload
    // so in formValue the corresponding field => impressionOption will be set to false
    const { getDiffRetainerLang, impressionOption } = useDiffRetainerLangContext();
    const getLang = useLang();
    if (isNewCase || isReSubmit) {
        // using hidden input field to set the form value for impressionOption field (false)
        return (
            <Controller
                name="impressionOption"
                control={control}
                defaultValue={false}
                render={({ field }) => <input {...field} type="hidden" />}
            />
        );
    }
    return (
        <CustomContainer style={{ display: !impressionType ? 'none' : 'block' }}>
            <h4>{getDiffRetainerLang(`formQuestionLabel.${question.name}`)}</h4>
            <RadioRhf
                name={question.name}
                control={control}
                defaultValue={defaultValue || null}
                options={question.option}
                label={getDiffRetainerLang(`formQuestionLabel.${question.name}`)}
                errors={errors}
                formControlLabelRhf={question.option.map((opt) => {
                    let renderOption = true;
                    switch (opt.value) {
                        case CaseImpressionFileType.LATEST_APPROVED_TREATMENT_PLAN:
                            if (!impressionOption.enableUseLastApprovedTp) {
                                renderOption = false;
                            }
                            break;
                        case CaseImpressionFileType.LATEST_RETAINER_REQUEST:
                            if (!impressionOption.enableUseLastRetainerRequest) {
                                renderOption = false;
                            }
                            break;
                        default:
                            break;
                    }
                    if (!renderOption) return null;
                    return <RadioOptionRhf key={opt.value} label={getLang(`@${opt.label}`)} value={opt.value} />;
                })}
            />

            {enableUploadImpression && (
                <NewPatientPhotos
                    tabMode="impression"
                    mode={undefined}
                    threeshapeConnected={threeshapeConnected}
                    handleThreeshapeConnected={(value) => setThreeshapeConnected(value)}
                />
            )}
        </CustomContainer>
    );
};

export default FileUploadQuestion;
